// @givmo/board — "Print the questions": a blank answer sheet with no name on it (CBG-YR4-ASSESS-WEB;
// the derivation §3.3, contract C14 — the web's print of the questions read, no PDF).
//
// The sheet is the assessment's questions as frozen at its opening, read fresh: each statement with
// its source and a box for each of the five answers, a few ruled lines under each group's comment
// prompt and each open question. It asks for no name, and the officer's later entry of it carries
// none (§4.2), so nothing on paper or in Board ties the answers to a person.
//
// ⚠ HOW IT PRINTS WITHOUT LEAVING THE PAGE. The console's content-security policy admits styles from
// its own stylesheet only (`style-src 'self'`), so the sheet cannot be a document of its own with
// inline styles. It is rendered into this page, in a body-level container shown only in print
// (`print:block`), and while it prints the body hides every other child (`PRINT_BODY_CLASS`), so the
// console's chrome, any dialog and any toast stay off the paper. Both classes live in this package's
// source, which the host's Tailwind scans.

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from '@givmo/design-system';
import type { BoardSelfAssessmentQuestionsOut } from '@givmo/api-client';
import { mailDate } from '../conflicts/coi-decode';
import { useSelfAssessmentQuestions } from './assessment-queries';
import { assessmentWords as w } from './assessment-words';

/** The sheet's container: off the screen, on the paper. */
export const PRINT_SHEET_CLASS = 'board-print-sheet hidden print:block';
/** On `<body>` while the sheet prints: every other child of the body stays off the paper. */
export const PRINT_BODY_CLASS = 'print:[&>*:not(.board-print-sheet)]:hidden';

const SHEET_TEXT = 'font-sans text-s14 leading-h15 text-black';

function Box() {
  return <span className="inline-block size-12 shrink-0 border border-black" aria-hidden />;
}

function Lines({ count }: { count: number }) {
  return (
    <div className="flex flex-col gap-18 pt-6" aria-hidden>
      {Array.from({ length: count }, (_, index) => (
        <span key={index} className="block h-12 border-b border-neutral-500" />
      ))}
    </div>
  );
}

/** The blank sheet itself. */
export function QuestionsSheet({
  questions,
  organization,
}: {
  questions: BoardSelfAssessmentQuestionsOut;
  organization: string | null;
}) {
  return (
    <div
      className="mx-auto flex max-w-[720px] flex-col gap-18 p-24"
      data-testid="assessment-print-sheet"
    >
      <header className="flex flex-col gap-4">
        <h1 className="font-sans text-s20 font-w700 text-black">
          {w.printHeading(questions.label)}
        </h1>
        {organization ? <p className={SHEET_TEXT}>{organization}</p> : null}
        <p className={SHEET_TEXT}>{w.printDue(mailDate(questions.due_on))}</p>
        <p className={SHEET_TEXT}>{w.printLine}</p>
      </header>
      {questions.groups.map((group) => (
        <section key={group.group_id} className="flex flex-col gap-12 break-inside-avoid-page">
          <h2 className="font-sans text-s13 font-w700 uppercase tracking-wide text-black">
            {group.title}
          </h2>
          {group.statements.map((statement) => (
            <div key={statement.statement_id} className="flex flex-col gap-6 break-inside-avoid">
              <p className={SHEET_TEXT}>{statement.text}</p>
              <ul className="flex flex-wrap gap-x-20 gap-y-6">
                {questions.answer_options.map((option) => (
                  <li key={option.answer} className={`flex items-center gap-6 ${SHEET_TEXT}`}>
                    <Box />
                    {option.label}
                  </li>
                ))}
              </ul>
            </div>
          ))}
          <div className="flex flex-col gap-2 break-inside-avoid">
            <p className={`${SHEET_TEXT} font-w600`}>{questions.comment_prompt}</p>
            <p className="font-sans text-s12 text-neutral-700">{questions.comment_help}</p>
            <Lines count={3} />
          </div>
        </section>
      ))}
      {(questions.open_questions ?? []).length > 0 ? (
        <section className="flex flex-col gap-12 break-inside-avoid-page">
          {questions.open_questions_title ? (
            <h2 className="font-sans text-s13 font-w700 uppercase tracking-wide text-black">
              {questions.open_questions_title}
            </h2>
          ) : null}
          {(questions.open_questions ?? []).map((question) => (
            <div key={question.question_id} className="flex flex-col gap-2 break-inside-avoid">
              <p className={`${SHEET_TEXT} font-w600`}>{question.text}</p>
              <Lines count={4} />
            </div>
          ))}
        </section>
      ) : null}
    </div>
  );
}

/**
 * The print act: `print(assessmentId)` reads the questions fresh, puts the sheet on the page for the
 * printer, asks the browser to print, and takes the sheet away when printing is done.
 */
export function useQuestionsPrint(orgRef: string | null, organization: string | null) {
  const [assessmentId, setAssessmentId] = useState<string | null>(null);
  const query = useSelfAssessmentQuestions(orgRef, assessmentId, assessmentId !== null);
  const questions = assessmentId !== null ? (query.data ?? null) : null;
  const printed = useRef<string | null>(null);

  useEffect(() => {
    if (assessmentId !== null && query.isError) {
      toast.error(w.couldNotPrint);
      setAssessmentId(null);
    }
  }, [assessmentId, query.isError]);

  useEffect(() => {
    if (questions === null || printed.current === questions.assessment_id) return;
    printed.current = questions.assessment_id;
    const classes = PRINT_BODY_CLASS.split(' ');
    const done = () => {
      document.body.classList.remove(...classes);
      window.removeEventListener('afterprint', done);
      printed.current = null;
      setAssessmentId(null);
    };
    document.body.classList.add(...classes);
    window.addEventListener('afterprint', done);
    window.print();
  }, [questions]);

  const sheet =
    questions === null
      ? null
      : createPortal(
          <div className={PRINT_SHEET_CLASS}>
            <QuestionsSheet questions={questions} organization={organization} />
          </div>,
          document.body,
        );

  return { print: (id: string) => setAssessmentId(id), sheet };
}
